import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import quoteService, { type ExtraInRateScope } from '../../../services/quote.service';
import { useModalDrag } from '../../../hooks/useModalDrag';
import ModalHeader from '../../../components/ui/ModalHeader';

interface Props {
  open: boolean;
  quoteNumber: number;
  revisionNumber: number;
  onClose: () => void;
}

/**
 * V1 "View/Send Quote" dialog (inline_quote.pl:3338-3691): pick recipients,
 * the backend renders the PDF and emails it via SendGrid, then stamps a
 * "QUOTE EMAILED TO …" note. Outside production the backend redirects all
 * mail to the configured test recipient.
 */
const SendQuoteDialog: React.FC<Props> = ({ open, quoteNumber, revisionNumber, onClose }) => {
  const [sendToUser, setSendToUser] = useState(true);
  const [sendToCsr, setSendToCsr] = useState(false);
  const [sendToPricing, setSendToPricing] = useState(false);
  const [sendToCustomer, setSendToCustomer] = useState(false);
  const [sendToOther, setSendToOther] = useState(false);
  const [otherEmail, setOtherEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string[] | null>(null);
  // N39: selectable EXTRA-IN-RATE combos for the PDF, fetched on open.
  const [rateScopes, setRateScopes] = useState<ExtraInRateScope[]>([]);
  const [checkedCombos, setCheckedCombos] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    quoteService.getExtraInRateCombos(quoteNumber, revisionNumber)
      .then((scopes) => {
        if (cancelled) return;
        setRateScopes(scopes);
        const pre = new Set<string>();
        scopes.forEach((s) => s.combos.forEach((c) => {
          if (c.checked) pre.add(`${s.tablename}|${s.tablerecno}|${c.recnolist}`);
        }));
        setCheckedCombos(pre);
      })
      .catch(() => { /* rate options simply don't render */ });
    return () => { cancelled = true; };
  }, [open, quoteNumber, revisionNumber]);

  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(open);

  if (!open) return null;

  const anySelected = sendToUser || sendToCsr || sendToPricing || sendToCustomer
    || (sendToOther && otherEmail.trim().length > 0);

  const submit = async () => {
    if (busy || !anySelected) return;
    setBusy(true);
    setError(null);
    try {
      // N39: persist the chosen rate-option combos before sending (V1
      // saveviewsendquote replaces them on send).
      if (rateScopes.length) {
        const selected = rateScopes.flatMap((s) => s.combos
          .filter((c) => checkedCombos.has(`${s.tablename}|${s.tablerecno}|${c.recnolist}`))
          .map((c) => ({ tablename: s.tablename, tablerecno: s.tablerecno, recnolist: c.recnolist })));
        await quoteService.putExtraInRateCombos(quoteNumber, revisionNumber, selected);
      }
      const result = await quoteService.sendQuote(quoteNumber, revisionNumber, {
        sendToUser, sendToCsr, sendToPricing, sendToCustomer, sendToOther,
        otherEmail: otherEmail.trim(),
      });
      setSentTo(result.recipients);
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      setError(typeof detail === 'string' && detail ? detail : 'Failed to send the quote.');
    } finally {
      setBusy(false);
    }
  };

  const check = (label: string, value: boolean, set: (v: boolean) => void) => (
    <label className="flex items-center gap-2 text-[0.78125rem] text-slate-700 dark:text-slate-300 cursor-pointer">
      <input type="checkbox" checked={value} onChange={(e) => set(e.target.checked)} />
      {label}
    </label>
  );

  return createPortal(
    // Portaled to <body> + z-[120] so it escapes the editor drawer's stacking
    // context (otherwise it renders behind the edit screen).
    <div className="fixed inset-0 z-[120] bg-slate-900/40 flex items-center justify-center" role="dialog" aria-label="Send Quote">
      <div
        ref={panelRef}
        style={panelStyle}
        className="bg-panel rounded-lg shadow-xl border border-slate-200 w-[400px] dark:border-gray-700"
      >
        <ModalHeader
          title={`Send Quote ${quoteNumber}-${revisionNumber}`}
          onClose={onClose}
          dragHandleProps={dragHandleProps}
        />
        <div className="px-4 pt-3 text-[0.71875rem] text-slate-500 dark:text-slate-400">
          Emails the quote PDF; a note is added to the quote with the recipients.
        </div>

        {sentTo ? (
          <div className="px-4 py-4">
            <div className="text-[0.78125rem] text-green-700 dark:text-green-400 font-semibold mb-1">Quote sent ✓</div>
            <div className="text-[0.75rem] text-slate-600 dark:text-slate-300 break-words">To: {sentTo.join(', ')}</div>
          </div>
        ) : (
          <div className="px-4 py-3 flex flex-col gap-2">
            {check('Me', sendToUser, setSendToUser)}
            {check('Sales / Whse CSRs', sendToCsr, setSendToCsr)}
            {check('Pricing (pricing@gwii.com)', sendToPricing, setSendToPricing)}
            {check('Customer (quote contact email)', sendToCustomer, setSendToCustomer)}
            <div className="flex items-center gap-2">
              {check('Other:', sendToOther, setSendToOther)}
              <input
                value={otherEmail}
                onChange={(e) => setOtherEmail(e.target.value)}
                placeholder="email@example.com"
                aria-label="Other recipient"
                className="flex-1 border border-slate-300 rounded px-2 py-1 text-[0.75rem] bg-white text-slate-900 dark:bg-gray-900 dark:text-gray-100 dark:border-gray-600"
              />
            </div>
            {rateScopes.length > 0 && (
              <div className="mt-1 border-t border-slate-100 dark:border-gray-700 pt-2">
                <div className="text-[0.6875rem] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">Rate Options</div>
                {rateScopes.map((s) => s.combos.map((c) => {
                  const key = `${s.tablename}|${s.tablerecno}|${c.recnolist}`;
                  return (
                    <label key={key} className="flex items-center gap-2 text-[0.75rem] text-slate-700 dark:text-slate-300 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={checkedCombos.has(key)}
                        onChange={(e) => setCheckedCombos((prev) => {
                          const next = new Set(prev);
                          if (e.target.checked) next.add(key); else next.delete(key);
                          return next;
                        })}
                      />
                      {(c.descriptions.join(' + ') || '(none)')} (${c.total.toFixed(2)})
                    </label>
                  );
                }))}
              </div>
            )}
            {error && <div className="text-[0.71875rem] text-red-600 dark:text-red-400">{error}</div>}
          </div>
        )}

        <div className="px-4 py-3 border-t border-slate-200 dark:border-gray-700 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-[0.75rem] text-slate-600 bg-panel border border-slate-300 rounded hover:bg-slate-50 dark:text-slate-300 dark:border-gray-600 dark:hover:bg-gray-700"
          >
            {sentTo ? 'Close' : 'Cancel'}
          </button>
          {!sentTo && (
            <button
              type="button"
              onClick={() => { void submit(); }}
              disabled={busy || !anySelected}
              className="px-3 py-1.5 text-[0.75rem] font-semibold text-white bg-blue-600 border border-blue-700 rounded disabled:opacity-40 hover:bg-blue-700"
            >
              {busy ? 'Sending…' : 'Send'}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default SendQuoteDialog;
